import { z } from "zod";

// ---------- enums ----------
/** 'retired' is gone (round 4): delete is the only way off the shelf. */
export const ItemStatus = z.enum(["unstarted", "in_progress", "done"]);
export type ItemStatus = z.infer<typeof ItemStatus>;

export const FocusLevel = z.enum(["light", "medium", "deep"]);
export type FocusLevel = z.infer<typeof FocusLevel>;

/** Mood KEYS reference the user's own editable moods table (round 2). */
export const MoodKey = z.string().regex(/^[a-z0-9_-]{1,40}$/);
export type MoodKey = z.infer<typeof MoodKey>;
/** The six browse kinds every shelf starts with (seed order = default order). */
export const DEFAULT_CATEGORIES: { kind: "book" | "video" | "article" | "listen" | "hobby" | "activity"; name: string; color: string }[] = [
  { kind: "book", name: "Books", color: "cat-4" },
  { kind: "video", name: "Videos", color: "cat-3" },
  { kind: "article", name: "Articles", color: "cat-7" },
  { kind: "listen", name: "Listening", color: "cat-5" },
  { kind: "hobby", name: "Hobbies", color: "cat-2" },
  { kind: "activity", name: "Activities", color: "cat-1" },
];

export const DEFAULT_MOODS: { key: string; name: string }[] = [
  { key: "calming", name: "Calming" },
  { key: "joyful", name: "Joyful" },
  { key: "energizing", name: "Energizing" },
  { key: "active", name: "Moving" },
  { key: "still", name: "Still" },
  { key: "mindful", name: "Mindful" },
];

/** Make something, take something in, or just be (D-24). */
export const Orientation = z.enum(["create", "consume", "learn", "fun", "be"]);
export type Orientation = z.infer<typeof Orientation>;

/** Shelf browse kinds — fixed default categories seeded per user (D-24). */
export const CategoryKind = z.enum(["book", "video", "article", "listen", "hobby", "activity", "custom"]);
export type CategoryKind = z.infer<typeof CategoryKind>;

export const PlaceKind = z.enum(["indoor", "outdoor"]);
export type PlaceKind = z.infer<typeof PlaceKind>;
export const WhereKind = z.enum(["anywhere", "indoor", "outdoor", "place"]);
export type WhereKind = z.infer<typeof WhereKind>;

/** auto = source/link thumbnail if any, else a seeded soft gradient (D-27). */
export const CoverKind = z.enum(["auto", "image", "emoji", "text", "none"]);
export type CoverKind = z.infer<typeof CoverKind>;
export const COVER_TEXT_MAX = 16;

export const Theme = z.enum(["system", "light", "dark"]);
export const TextSize = z.enum(["system", "s", "m", "l", "xl"]);
export const Density = z.enum(["comfortable", "compact"]);
export const Browsing = z.enum(["continuous", "paged"]);
export const Motion = z.enum(["system", "reduced", "full"]);
export const Pack = z.enum(["sage", "clay", "slate", "plum", "mono"]);
export const Locale = z.enum(["system", "en", "it"]);
export const PickMode = z.enum(["shuffle", "ai"]);
export const FeedbackCategory = z.enum(["bug", "idea", "other"]);

// ---------- primitives ----------
export const Id = z.string().uuid();
const HttpUrl = z
  .string()
  .max(2048)
  .url()
  .refine((u) => u.startsWith("http://") || u.startsWith("https://"), "http(s) only");

// Requirements §4.1. `description` is Markdown (whitelisted subset, rendered
// sanitized client-side); `annotation` is deliberately plain text.
export const ITEM_TITLE_MAX = 300;
export const ITEM_DESCRIPTION_MAX = 20_000;
export const Item = z.object({
  id: Id,
  title: z.string().min(1).max(ITEM_TITLE_MAX),
  link: HttpUrl.nullable(),
  description: z.string().max(ITEM_DESCRIPTION_MAX),
  annotation: z.string().max(5_000),
  categoryId: Id.nullable(),
  tagIds: z.array(Id).max(30),
  needIds: z.array(Id).max(10),
  status: ItemStatus,
  focus: FocusLevel.nullable(),
  moods: z.array(MoodKey).max(20),
  orientation: Orientation.nullable(),
  /** Smallest meaningful chunk of time. Eligibility keys on this. */
  durationMin: z.number().int().min(1).max(6000).nullable(),
  /** null = open-ended ("20 min +"); = durationMin for fixed lengths. */
  durationMaxMin: z.number().int().min(1).max(6000).nullable(),
  tracksProgress: z.boolean(),
  whereKind: WhereKind,
  placeId: Id.nullable(),
  coverKind: CoverKind,
  coverValue: z.string().max(32).nullable(),
  coverSeed: z.number().int(),
  /** Same-origin URL of the stored cover image (null unless coverKind=image). */
  coverUrl: z.string().nullable(),
  coverLowRes: z.boolean(),
  thumbUrl: HttpUrl.nullable(),
  sourceId: Id.nullable(),
  externalId: z.string().max(200).nullable(),
  pinned: z.boolean(),
  pickCount: z.number().int().min(0),
  lastPickedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Item = z.infer<typeof Item>;

const itemEditable = {
  title: true, link: true, description: true, annotation: true, categoryId: true, tagIds: true,
  needIds: true, focus: true, moods: true, orientation: true, durationMin: true, durationMaxMin: true,
  tracksProgress: true, thumbUrl: true, whereKind: true, placeId: true, coverKind: true, coverValue: true,
} as const;

export const ItemCreate = Item.pick(itemEditable).partial({
  link: true, description: true, annotation: true, categoryId: true, tagIds: true, needIds: true,
  focus: true, moods: true, orientation: true, durationMin: true, durationMaxMin: true, tracksProgress: true,
  thumbUrl: true, whereKind: true, placeId: true, coverKind: true, coverValue: true,
});
export type ItemCreate = z.infer<typeof ItemCreate>;

export const ItemUpdate = ItemCreate.extend({
  status: ItemStatus,
  pinned: z.boolean(),
  // optimistic-concurrency guard for the offline outbox (D-8)
  ifUpdatedAt: z.string().datetime(),
}).partial();
export type ItemUpdate = z.infer<typeof ItemUpdate>;

export const BulkItemAction = z.object({
  itemIds: z.array(Id).min(1).max(200),
  action: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("setStatus"), status: ItemStatus }),
    z.object({ kind: z.literal("setCategory"), categoryId: Id.nullable() }),
    z.object({ kind: z.literal("setPinned"), pinned: z.boolean() }),
    z.object({ kind: z.literal("addTag"), tagId: Id }),
    z.object({ kind: z.literal("removeTag"), tagId: Id }),
    z.object({ kind: z.literal("delete") }),
    // Bulk edit carries every facet (D-37): set = replace with the given value/list; an empty list clears.
    z.object({ kind: z.literal("setMoods"), moods: z.array(MoodKey).max(20) }),
    z.object({ kind: z.literal("setOrientation"), orientation: Orientation.nullable() }),
    z.object({ kind: z.literal("setNeeds"), needIds: z.array(Id).max(10) }),
    z.object({ kind: z.literal("setWhere"), whereKind: WhereKind, placeId: Id.nullable().optional() }),
  ]),
});
export type BulkItemAction = z.infer<typeof BulkItemAction>;

export const ItemListQuery = z.object({
  categoryId: Id.optional(),
  tagId: Id.optional(),
  needId: Id.optional(),
  placeId: Id.optional(),
  status: ItemStatus.optional(),
  sourceId: Id.optional(),
  focus: FocusLevel.optional(),
  duration: z.enum(["short", "medium", "long"]).optional(), // <15, 15–45, 45+
  needsAnnotation: z.coerce.boolean().optional(),
  ids: z.string().max(2000).optional(), // comma-separated, for deals/selections
  /** items that arrived from a source after this moment (the "new since last time" note) */
  createdAfter: z.string().datetime().optional(),
  fromSources: z.coerce.boolean().optional(),
  q: z.string().max(200).optional(),
  sort: z.enum(["recent", "title", "leastPicked", "duration"]).default("recent"),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ItemListQuery = z.infer<typeof ItemListQuery>;

export const Category = z.object({
  id: Id,
  name: z.string().min(1).max(60),
  colorToken: z.enum(["cat-1", "cat-2", "cat-3", "cat-4", "cat-5", "cat-6", "cat-7", "cat-8"]),
  sortOrder: z.number().int(),
  parentId: Id.nullable(),
  kind: CategoryKind,
  itemCount: z.number().int().min(0).optional(),
});
export type Category = z.infer<typeof Category>;

export const Tag = z.object({ id: Id, name: z.string().min(1).max(60), usageCount: z.number().int().min(0) });
export type Tag = z.infer<typeof Tag>;

export const Need = z.object({
  id: Id,
  name: z.string().min(1).max(60),
  builtIn: z.boolean(),
  usageCount: z.number().int().min(0),
});
export type Need = z.infer<typeof Need>;

/** A user's own "feels like" vocabulary — rename, reorder, remove, add. */
export const Mood = z.object({
  id: Id,
  key: MoodKey,
  name: z.string().min(1).max(40),
  builtIn: z.boolean(),
  sortOrder: z.number().int(),
});
export type Mood = z.infer<typeof Mood>;
export const MoodCreate = z.object({ name: z.string().min(1).max(40) });
export const MoodUpdate = z.object({ name: z.string().min(1).max(40).optional(), sortOrder: z.number().int().optional() });

// Coordinates are OPTIONAL and only ever user-saved (Requirements §5.10);
// the current position never reaches the server. A map-picked point with a
// radius is a choice, not a track.
export const Place = z.object({
  id: Id,
  name: z.string().min(1).max(60),
  kind: PlaceKind,
  lat: z.number().min(-90).max(90).nullable(),
  lng: z.number().min(-180).max(180).nullable(),
  radiusM: z.number().int().min(50).max(5000).nullable(),
  needIds: z.array(Id).max(50),
  itemCount: z.number().int().min(0).optional(),
});
export type Place = z.infer<typeof Place>;

/** The Home question's state — remembered on the device, sent with each shuffle. */
export const RestFilters = z.object({
  /** minutes; null = any */
  timeMin: z.number().int().min(1).max(600).nullable().default(null),
  moods: z.array(MoodKey).max(20).default([]),
  orientations: z.array(Orientation).max(5).default([]),
  /** undefined = needs feature off / not filtering; [] = nothing on hand */
  haveNeedIds: z.array(Id).max(50).optional(),
  where: z.object({ kind: WhereKind, placeId: Id.optional() }).default({ kind: "anywhere" }),
});
export type RestFilters = z.infer<typeof RestFilters>;
export const defaultFilters = (): RestFilters => RestFilters.parse({});

export const ShuffleRequest = z.object({
  count: z.number().int().min(1).max(5).default(3),
  filters: RestFilters.default({}),
  includeDone: z.boolean().default(false),
  /** never deal these (redraw one card / add a card to a deal) */
  excludeItemIds: z.array(Id).max(50).optional(),
  /** false = draw only, don't touch the persisted deal (redraw / add) */
  persist: z.boolean().default(true),
});
export type ShuffleRequest = z.infer<typeof ShuffleRequest>;

export interface ShuffleResponse {
  pickId: string | null;
  itemIds: string[];
  relaxed: boolean;
}

/** The persistent deal (D-25): survives restarts and devices until reshuffled or saved. */
export const Deal = z.object({
  pickId: Id.nullable(),
  itemIds: z.array(Id).max(5),
  filters: RestFilters,
  relaxed: z.boolean().default(false),
  createdAt: z.string().datetime(),
});
export type Deal = z.infer<typeof Deal>;
export const DealPut = Deal.pick({ itemIds: true }).extend({ relaxed: z.boolean().optional() });

export const Selection = z.object({
  id: Id,
  title: z.string().min(1).max(120),
  itemIds: z.array(Id).min(1).max(5),
  origin: z.enum(["user", "ai"]),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Selection = z.infer<typeof Selection>;
export const SelectionCreate = Selection.pick({ title: true, itemIds: true });
export type SelectionCreate = z.infer<typeof SelectionCreate>;
export const SelectionUpdate = SelectionCreate.partial();
export type SelectionUpdate = z.infer<typeof SelectionUpdate>;

export const Pick = z.object({
  id: Id,
  mode: PickMode,
  createdAt: z.string().datetime(),
  items: z.array(
    z.object({ itemId: Id, offered: z.boolean(), chosen: z.boolean(), reason: z.string().max(300).nullable() })
  ),
});
export type Pick = z.infer<typeof Pick>;

export const Settings = z.object({
  appearance: z.object({
    theme: Theme.default("system"),
    pack: Pack.default("sage"),
    textSize: TextSize.default("system"),
    density: Density.default("comfortable"),
    browsing: Browsing.default("continuous"),
    thumbnails: z.boolean().default(true),
    motion: Motion.default("system"),
    highContrast: z.boolean().default(false),
  }).default({}),
  locale: Locale.default("system"),
  rest: z.object({
    /** cards a shuffle deals by default (round 2) */
    defaultCards: z.number().int().min(1).max(5).default(3),
    /** the most a deal or selection may hold — never above the Rest law's 5 */
    maxCards: z.number().int().min(1).max(5).default(5),
    includeDone: z.boolean().default(false),
    followup: z.boolean().default(true),
  }).default({}),
  notifications: z.object({ syncFailEmail: z.boolean().default(false) }).default({}),
  features: z.object({
    context: z.boolean().default(false),
    needs: z.boolean().default(false),
    pickHistory: z.boolean().default(false),
    /** the floating "report a problem or idea" button on every screen (beta default: on) */
    feedbackButton: z.boolean().default(true),
  }).default({}),
});
export type Settings = z.infer<typeof Settings>;
export const defaultSettings = (): Settings => Settings.parse({});

export const Feedback = z.object({
  category: FeedbackCategory.default("other"),
  body: z.string().min(1).max(5000),
  diagnostics: z
    .object({
      appVersion: z.string().max(40).optional(),
      route: z.string().max(200).optional(),
      platform: z.string().max(200).optional(),
      locale: z.string().max(20).optional(),
    })
    .default({}),
  emailBack: z.boolean().default(false),
});
export type Feedback = z.infer<typeof Feedback>;

// ---------------------------------------------------------------- sources
/** D-39: a Spotify show becomes a `feed` source (its public RSS); no Spotify type. */
export const SourceType = z.enum(["youtube", "feed"]);
export type SourceType = z.infer<typeof SourceType>;
/** What a source's items are — decides the category they land in. */
export const SourceKind = z.enum(["video", "podcast", "article", "music"]);
export type SourceKind = z.infer<typeof SourceKind>;
export const CATEGORY_FOR_KIND: Record<SourceKind, "video" | "listen" | "article"> = { video: "video", podcast: "listen", article: "article", music: "listen" };

/**
 * What a list applies to everything it adds (feedback F7b, D-36): the same
 * facets the item form has, chosen once per source. Applied to NEW items
 * only — an item already on the shelf is never touched, edited or not.
 * `categoryId` unset = the kind → category rule; `whereKind` "place" needs a
 * `placeId` of the caller's own (validated server-side, like an item's).
 */
export const SourceDefaults = z.object({
  categoryId: Id.nullable().optional(),
  moods: z.array(MoodKey).max(20).optional(),
  orientation: Orientation.nullable().optional(),
  whereKind: WhereKind.optional(),
  placeId: Id.nullable().optional(),
  needIds: z.array(Id).max(10).optional(),
  tracksProgress: z.boolean().optional(),
});
export type SourceDefaults = z.infer<typeof SourceDefaults>;

export const Source = z.object({
  id: Id,
  type: SourceType,
  kind: SourceKind.default("video"),
  label: z.string().min(1).max(120),
  enabled: z.boolean(),
  /** null = the kind rule only (D-36) */
  defaults: SourceDefaults.nullable().default(null),
  lastSyncAt: z.string().datetime().nullable(),
  lastStatus: z.enum(["ok", "partial", "error"]).nullable(),
  lastError: z.string().nullable(),
  itemCount: z.number().int().min(0),
  createdAt: z.string().datetime(),
});
export type Source = z.infer<typeof Source>;

export const SourceCreate = z.object({
  /** omitted = detect from the url (round 5: one paste box) */
  type: SourceType.optional(),
  /** playlist URL, page or feed URL, Spotify show link (resolved to its feed, D-39) — the connector validates/normalizes */
  url: z.string().min(1).max(2000),
  /** upstream ids the user unticked in the preview; never imported, never re-added */
  exclude: z.array(z.string().max(500)).max(500).default([]),
  /** facets for everything this list adds (D-36); omitted = the kind rule only */
  defaults: SourceDefaults.nullable().optional(),
});
export type SourceCreate = z.infer<typeof SourceCreate>;

export const SourceUpdate = z.object({
  label: z.string().min(1).max(120).optional(),
  enabled: z.boolean().optional(),
  /** changes reach only the items the source adds from now on (D-36); null clears */
  defaults: SourceDefaults.nullable().optional(),
});
export type SourceUpdate = z.infer<typeof SourceUpdate>;

export const SourcePreview = z.object({
  type: SourceType,
  kind: SourceKind,
  label: z.string(),
  total: z.number().int().min(0),
  /** the first page in full, so the import can be trimmed before it happens */
  entries: z.array(z.object({ externalId: z.string(), title: z.string(), thumbUrl: z.string().nullable(), durationMin: z.number().nullable() })).max(200),
});
export type SourcePreview = z.infer<typeof SourcePreview>;

export interface SyncStats {
  imported: number;
  /** items removed because they vanished upstream (round 4) */
  removed: number;
  total: number;
  notModified: boolean;
}

export const EnrichRequest = z.object({ url: z.string().url().max(2000) });
export type EnrichRequest = z.infer<typeof EnrichRequest>;

export interface EnrichResult {
  title: string | null;
  description: string | null;
  thumbUrl: string | null;
  siteName: string | null;
  /** known for YouTube videos (Data API) — the form fills an empty length with it */
  durationMin?: number | null;
}

export const ErrorCode = z.enum([
  "UNAUTHORIZED",
  "FORBIDDEN",
  "NOT_FOUND",
  "VALIDATION",
  "CONFLICT",
  "QUOTA_EXCEEDED",
  "RATE_LIMITED",
  "SHUFFLE_FALLBACK",
  "SOURCE_UNREACHABLE",
  /** a pasted link is a fine item but not a feed/playlist (round 5 quick entry) */
  "NOT_A_SOURCE",
  /** a Spotify show whose public feed the podcast directory does not know (D-39): paste the RSS link instead */
  "SHOW_FEED_NOT_FOUND",
  "BETA_INVITE_REQUIRED",
  /** the same identity signed in within 30 days of deleting its account (D-33) */
  "ACCOUNT_DELETED",
  "INTERNAL",
  /** the database lacks a table/column the code expects (a migration is pending) — 503, never a bare 500 */
  "SCHEMA_BEHIND",
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export interface ApiError {
  error: { code: ErrorCode; message: string; requestId?: string };
}

// per-user resource caps (Requirements §4.3) — enforced server-side
export const LIMITS = {
  items: 10_000,
  sources: 20,
  categories: 50,
  tags: 500,
  needs: 100,
  places: 20,
  moods: 20,
  selections: 100,
  restCardsMax: 5,
  /** stored cover: the largest size the UI ever renders (Architecture §image) */
  coverWidth: 960,
  coverHeight: 540,
  coverUploadBytes: 8 * 1024 * 1024,
} as const;

/**
 * The "no image" gradient (round 4): two soft stops from a curated, muted
 * range, chosen by the item's seed so it never changes and is never garish.
 * Shared so the API (export) and the client agree.
 */
export function coverGradient(seed: number): { from: string; to: string; angle: number } {
  const hues = [95, 150, 200, 245, 20, 40, 330, 175]; // sage, teal, slate, plum, clay, ochre, rose, sea
  const h = hues[Math.abs(seed) % hues.length]!;
  const h2 = (h + 25 + (Math.abs(seed >> 3) % 20)) % 360;
  const angle = 120 + (Math.abs(seed >> 5) % 60);
  return { from: `hsl(${h} 22% 84%)`, to: `hsl(${h2} 18% 72%)`, angle };
}

// ---------------------------------------------------------------- consents
/** The version date printed on /privacy and /terms; bump it when either
 *  text changes and the web app records a fresh acceptance (D-38). */
export const TERMS_VERSION = "2026-09-05";
/** `POST /v1/consents` — the only kind a client may record itself: accepting
 *  the terms and privacy notice. The connector consent is recorded by the
 *  flow itself (D-26). */
export const ConsentCreate = z.object({
  kind: z.literal("terms"),
  version: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type ConsentCreate = z.infer<typeof ConsentCreate>;
